import { useEffect, useRef } from "react";
import axios from "axios";

import AuthDao from "./AuthDao"; // This DAO's options type is missing `signal`
import ServiceWrapper from "@utils/ServiceWrapper";

import { EXPIRY_BUFFER_MILLI, SAML_LOGOUT_URL, START_SESSION_URL, TOKEN_EXPIRY_CHECK_MILLI } from "./config";
import { getDateFromEpoch } from "@utils/DateFormatter";
import { IS_PRODUCTION } from "@/const/Environment";
import { useGlobalContext } from "@components/contextProvider/ContextProvider";
import {
    getTokenData,
    setTokenData,
    clearToken,
    getPublicTokenData,
    setPublicTokenData,
    type TokenData
} from "./TokenStorage";

// HACK: The AuthDaoOptions type in AuthDao.tsx is missing the `signal` property.
// This is a temporary workaround to satisfy the TypeScript compiler without modifying the
// un-provided DAO file. The correct fix is to add `signal?: AbortSignal;` to the
// AuthDaoOptions interface in `utils/auth/AuthDao.tsx`.
type PatchedAuthDaoOptions = {
    action: "tokenRead" | "tokenReadDev";
    username?: string | null;
    signal?: AbortSignal;
};

const PatchedAuthDao = AuthDao as (options: PatchedAuthDaoOptions) => Promise<{ data: unknown } | null>;

const DEV_USERNAME_KEY = "uname";
const DEV_LOGIN_KEY = "devLogin";

const setCookie = (cname: string, cvalue: string, exHours: number): void => {
    const d = new Date();
    d.setTime(d.getTime() + exHours * 60 * 60 * 1000);
    const expires = `expires=${d.toUTCString()}`;
    document.cookie = `${cname}=${cvalue};domain=.dcsdk12.org;${expires};path=/`;
};

const getCookie = (cname: string): string => {
    const name = `${cname}=`;
    const ca = document.cookie.split(";");
    for (let c of ca) {
        while (c.startsWith(" ")) {
            c = c.substring(1);
        }
        if (c.startsWith(name)) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
};

const checkCookie = (cname: string): boolean => {
    const interrupt = getCookie(cname);
    if (interrupt !== "") {
        return true;
    }
    setCookie(cname, "1", 4);
    return false;
};

/**
 * Logs the user out: clears token, session, and redirects to SAML logout.
 */
const logout = (): void => {
    clearToken();
    sessionStorage.clear();
    sessionStorage.setItem("logout", Date.now().toString());
    globalThis.location.replace(SAML_LOGOUT_URL);
};

/**
 * Decodes a JWT and manually parses the payload.
 */
const parseJwt = (token: string): Record<string, unknown> => {
    if (!token || typeof token !== "string") {
        console.error("Invalid token provided to parseJwt");
        return {};
    }

    try {
        const base64Url = token.split(".")[1];
        if (!base64Url) {
            console.error("Invalid JWT structure");
            return {};
        }
        const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");
        const jsonPayload = decodeURIComponent(
            globalThis
                .atob(base64)
                .split("")
                .map((c: string): string => {
                    const codePoint = c.codePointAt(0);
                    if (codePoint === undefined) return "";
                    const sliceString = `00${codePoint.toString(16)}`.slice(-2);
                    return `%${sliceString}`;
                })
                .join("")
        );
        return JSON.parse(jsonPayload) as Record<string, unknown>;
    } catch {
        console.error("Failed to parse JWT");
        return {};
    }
};

const extractDevJwt = (rawData: unknown): string => {
    if (typeof rawData === "string") return rawData;
    if (rawData && typeof rawData === "object") {
        const data = rawData as { token?: string; payload?: string };
        return data.token ?? data.payload ?? "";
    }
    return "";
};

const extractProdJwt = (rawData: unknown): string => {
    if (!rawData || typeof rawData !== "object") return "";
    const data = rawData as { payload?: { jwt?: string; token?: string } | string };
    if (typeof data.payload === "string") return data.payload;
    return data.payload?.jwt ?? data.payload?.token ?? "";
};

const extractUsername = (parsed: Record<string, unknown>): string => {
    if (typeof parsed.username === "string") return parsed.username;
    if (typeof parsed.sub === "string") return parsed.sub;
    return "";
};

// JWT expiry is in seconds, but JS Date works in milliseconds.
const JWT_EXP_IN_SECONDS_THRESHOLD = 1000000000000;

/**
 * Processes an API response and stores the extracted token.
 */
const processTokenResponse = (
    rawData: unknown,
    isDev = false,
    storeFn: (data: TokenData) => void = setTokenData
): TokenData | null => {
    if (!rawData) return null;

    const jwt = isDev ? extractDevJwt(rawData) : extractProdJwt(rawData);
    if (!jwt) return null;

    if (jwt.split(".").length !== 3) {
        console.error("Invalid JWT structure");
        return null;
    }

    const parsed = parseJwt(jwt);
    const exp = typeof parsed.exp === "number" ? parsed.exp : 0;
    const username = extractUsername(parsed);
    const guid = typeof parsed.guid === "string" ? parsed.guid : "";

    try {
        const tokenData: TokenData = {
            token: jwt,
            expiry: exp < JWT_EXP_IN_SECONDS_THRESHOLD ? exp * 1000 : exp,
            readableExpiry: getDateFromEpoch(exp.toString()),
            username,
            guid,
            tokenType: "Bearer"
        };
        storeFn(tokenData);
        return tokenData;
    } catch (error) {
        console.error("Error creating token object:", error);
        return null;
    }
};

/**
 * Fetch Token (Production).
 */
const retrieveToken = async (signal?: AbortSignal): Promise<TokenData | null> => {
    if (!getTokenData() && !checkCookie("interrupt")) {
        globalThis.location.replace("/login-welcome");
        return null;
    }

    try {
        const response = await PatchedAuthDao({ action: "tokenRead", signal });
        return processTokenResponse((response as { data: unknown })?.data);
    } catch (error: unknown) {
        if (axios.isCancel(error)) {
            // This is an expected event in React 18 Strict Mode, log as info not warning.
            console.warn("Token retrieval cancelled.");
            return null;
        }
        if (error instanceof Error && error.message.includes("401")) {
            globalThis.location.replace(START_SESSION_URL);
        }
        return null;
    }
};

/**
 * Fetch Token (Development).
 */
const retrieveTokenDev = async (
    delay = 0,
    userOverride: string | null = null,
    signal?: AbortSignal
): Promise<TokenData | null> => {
    if (delay > 0) {
        await ServiceWrapper.wait(delay);
    }

    const username = userOverride ?? sessionStorage.getItem(DEV_USERNAME_KEY);
    if (!username) {
        console.error("No username available for dev token retrieval");
        return null;
    }

    try {
        const response = await PatchedAuthDao({ action: "tokenReadDev", username, signal });
        return processTokenResponse((response as { data: unknown })?.data, true);
    } catch (error: unknown) {
        if (axios.isCancel(error)) {
            // This is an expected event in React 18 Strict Mode, log as info not warning.
            console.warn("Dev token retrieval cancelled.");
            return null;
        }
        if (IS_PRODUCTION && error instanceof Error && error.message.includes("401")) {
            globalThis.location.replace(START_SESSION_URL);
        }
        return null;
    }
};

/**
 * Fetch Token (Public User).
 * Targets the public-user endpoint that exists in both dev and production
 * (the host resolves via SERVICE_HOST). Stores to the isolated public token
 * slot so it never overwrites an authenticated user's inMemoryToken.
 */
const retrievePublicToken = async (signal?: AbortSignal): Promise<TokenData | null> => {
    try {
        const response = await PatchedAuthDao({ action: "tokenReadDev", username: "public-user", signal });
        return processTokenResponse((response as { data: unknown })?.data, true, setPublicTokenData);
    } catch (error: unknown) {
        if (axios.isCancel(error)) {
            // This is an expected event in React 18 Strict Mode, log as info not warning.
            console.warn("Public token retrieval cancelled.");
        }
        return null;
    }
};

const readToken = (): TokenData | null => getTokenData();

const fakeLogout = (): void => {
    clearToken();
    sessionStorage.removeItem("user_details");
};

/**
 * React Component: Auth
 * Silently manages the token lifecycle (refresh interval).
 * Does not render any UI.
 */
const Auth = ({ isPublic = false }: { isPublic?: boolean }): null => {
    const { dispatch, state } = useGlobalContext();
    const { username } = state ?? {};
    const isFetching = useRef(false);

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;

        const checkAndRefreshToken = async (): Promise<void> => {
            if (isFetching.current || signal.aborted) return;

            const now = Date.now();
            const currentToken = isPublic ? getPublicTokenData() : getTokenData();
            const tokenExpired = !currentToken || currentToken.expiry - Number(EXPIRY_BUFFER_MILLI) < now;

            if (!tokenExpired) return;

            isFetching.current = true;
            try {
                let newToken: TokenData | null;
                if (isPublic) {
                    newToken = await retrievePublicToken(signal);
                } else {
                    const isDevMode = sessionStorage.getItem(DEV_LOGIN_KEY) === DEV_LOGIN_KEY;
                    newToken = isDevMode ? await retrieveTokenDev(0, username, signal) : await retrieveToken(signal);
                }

                if (newToken && !signal.aborted) {
                    dispatch({ type: "Token", token: newToken.token });
                    if (newToken.username) {
                        dispatch({ type: "Username", username: newToken.username });
                    }
                }
            } catch (error: unknown) {
                if (!axios.isCancel(error)) {
                    console.error("Token refresh failed", error);
                }
            } finally {
                isFetching.current = false;
            }
        };

        // Run immediately on mount, then on interval
        void checkAndRefreshToken();
        const intervalId = setInterval(() => {
            void checkAndRefreshToken();
        }, TOKEN_EXPIRY_CHECK_MILLI);

        return (): void => {
            controller.abort();
            clearInterval(intervalId);
        };
    }, [dispatch, isPublic, username]);

    return null;
};

export { Auth, fakeLogout, logout, readToken, retrievePublicToken, retrieveToken, retrieveTokenDev };
